import React from 'react';
import { Box, Card, IconButton, Badge, Stack, Tooltip, Typography, Link as MuiLink } from '@mui/material';
import { keyframes } from '@emotion/react';
import { Link as RouterLink } from 'react-router-dom';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import type { FleetDevice, FleetEventEntry } from '../../api/config';
import { HeartbeatSparkline } from './HeartbeatSparkline';
import { EventVolumeChart, computeEventTotals } from './EventVolumeChart';
import { isKioskMode } from '../../utils/kioskMode';

// Kiosk players (FireStick at the time of writing) paint-throttle aggressively
// when the page is "quiet"; the solar-low-vin flash gets stuck on whichever
// keyframe the throttle samples and ends up looking choppy or static at a
// random color. Render it as solid red on the kiosk so the alert reads as a
// stable, urgent state rather than a malfunction.
const FLASH_ENABLED = !isKioskMode();

// Per-device freshness thresholds. The location card itself is neutral —
// each device row carries its own freshness color so a multi-device site
// can show "device A is fine, device B is silent" without the card lying
// either way.
const FRESH_THRESHOLD_MIN = 60;
const LATE_THRESHOLD_MIN = 120;
const SILENT_THRESHOLD_MIN = 4 * 60;
const PENDING_GRACE_MIN = 60;

const SOLAR_BLINK_TRIGGER_V = 12.0;
const SOLAR_BLINK_CLEAR_V = 12.1;

// Layout primitives. These pin the heights of the heartbeat/event charts and
// derive both rendering and stride math from the same source so they can't
// drift apart. Kept above DeviceRow because the row uses them.
//
// FleetDashboardPage's grid uses `gridAutoRows: 8 px` + `gap: 8 px`, and a
// single-device card spans 8 rows → 8 × 8 + 7 × 8 = 120 px tall, with a
// 128 px stride between adjacent stacked cards. The multi-device card
// preserves that stride internally by giving each subsequent device row an
// extra top margin that consumes one full single-card stride.
const GRID_ROW_HEIGHT_PX = 8;
const GRID_GAP_PX = 8;
const ROWS_PER_UNIT = 8;
const SINGLE_CARD_HEIGHT_PX =
  ROWS_PER_UNIT * GRID_ROW_HEIGHT_PX + (ROWS_PER_UNIT - 1) * GRID_GAP_PX;
export const SINGLE_CARD_STRIDE_PX = SINGLE_CARD_HEIGHT_PX + GRID_GAP_PX;

// Device-row internal heights — single source of truth for both the rendered
// chart heights and the stride math below. The SVGs inside HeartbeatSparkline
// / EventVolumeChart pin to their `height` prop, so these stay accurate
// regardless of container width.
const HEARTBEAT_HEIGHT_PX = 26;
const CHART_SPACER_PX = 3;
// EventVolumeChart wrapper height (1 px border top + 1 px border bottom +
// HEARTBEAT_HEIGHT_PX-tall chart inside, clipped to fit).
const EVENT_CHART_BOX_HEIGHT_PX = 28;
const DEVICE_ROW_PADDING_Y_PX = 4; // py: 0.25 → 2 + 2
const DEVICE_ROW_HEADER_HEIGHT_PX = 18; // header Stack minHeight
const DEVICE_ROW_HEADER_GAP_PX = 2; // gap between header and charts
const DEVICE_ROW_CHARTS_HEIGHT_PX =
  HEARTBEAT_HEIGHT_PX + CHART_SPACER_PX + EVENT_CHART_BOX_HEIGHT_PX;
const DEVICE_ROW_HEIGHT_PX =
  DEVICE_ROW_PADDING_Y_PX +
  DEVICE_ROW_HEADER_HEIGHT_PX +
  DEVICE_ROW_HEADER_GAP_PX +
  DEVICE_ROW_CHARTS_HEIGHT_PX;
// LocationCard's flex-column gap (gap=0.5 = 4 px) sits between device rows.
const CARD_FLEX_GAP_PX = 4;
// Extra top margin applied to subsequent device rows so the row-to-row
// stride inside a multi-device card matches the single-card stride exactly.
// Derived: stride = device_row_height + flex_gap + mt → mt = stride - row - gap.
const DEVICE_ROW_STRIDE_MARGIN =
  SINGLE_CARD_STRIDE_PX - DEVICE_ROW_HEIGHT_PX - CARD_FLEX_GAP_PX;

export type DeviceState = 'pending' | 'green' | 'yellow' | 'red' | 'gray' | 'solar_low_vin';

export const STATE_COLOR: Record<DeviceState, string> = {
  pending: '#37474f',  // blue-gray 800 — strong neutral for the boundary
  green: '#1b5e20',    // green 900
  yellow: '#ca8a04',   // gold (HSL ~46°, 95%, 41%) — clearly yellow-family, lighter than dark mustard but still dark enough to read as a boundary
  red: '#b71c1c',      // red 900
  gray: '#424242',     // gray 800
  solar_low_vin: '#b71c1c', // static fallback if animation isn't running (e.g. prefers-reduced-motion)
};

export const STATE_TINT: Record<DeviceState, string> = {
  pending: '#eceff1',
  green: '#e8f5e9',
  yellow: '#fff8e1',
  red: '#ffebee',
  gray: '#bdbdbd',  // gray 400 — distinctly darker than the other tints so "long-silent" reads as off, while keeping body text at AAA contrast (7.45:1 vs text.primary)
  solar_low_vin: '#ffebee', // static fallback (red tint)
};

// Hard-alternating flash for solar low-vin alert. step-end timing means no
// interpolation: hold gray for 1.4 s, snap to red, hold red for 1.4 s, snap
// back. Animates background AND border so the location card's outlined frame
// pulses too. The whole row (or whole card, for worst-state) flashes; static
// content inside (text, icons) is unaffected.
const solarFlash = keyframes`
  0%, 100% {
    background-color: ${STATE_TINT.gray};
    border-color: ${STATE_COLOR.gray};
  }
  50% {
    background-color: ${STATE_TINT.red};
    border-color: ${STATE_COLOR.red};
  }
`;

function minutesAgo(iso: string | null, nowMs: number): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return (nowMs - ms) / 60000;
}

export function pickDeviceState(device: FleetDevice, nowMs: number, isSolarAlert: boolean): DeviceState {
  // Solar low-vin sits at the top of the stack — it overrides every other
  // state, including gray. A solar device that is both silent AND last
  // reported a low vin reading is doubly-broken; the flash is the louder
  // signal of the two and shouldn't be suppressed.
  if (isSolarAlert) return 'solar_low_vin';
  const ageMin = minutesAgo(device.last_seen_at, nowMs);
  if (ageMin === null) {
    const createdAgeMin = minutesAgo(device.created_at, nowMs);
    if (createdAgeMin !== null && createdAgeMin < PENDING_GRACE_MIN) return 'pending';
    // Never-heard-from device past the pending grace: same de-escalated
    // gray as a long-silent device. "Red" is for actively-degrading; a
    // device that was provisioned and never spoke is a known-offline.
    return 'gray';
  }
  if (ageMin < FRESH_THRESHOLD_MIN) return 'green';
  if (ageMin < LATE_THRESHOLD_MIN) return 'yellow';
  if (ageMin < SILENT_THRESHOLD_MIN) return 'red';
  return 'gray';
}

// Severity ranking for "worst-of-N" at the location level. solar_low_vin
// dominates everything (a panel-disconnected solar gateway is the most urgent
// state we surface). Then gray (long-silent or never-heard — a device that's
// been dark for hours is more troubling than one that's freshly degrading),
// then red (active concern, 2-4 h silent) → yellow (1-2 h late) → pending
// (brand new, expected) → green. The card shows the worst state so a wall
// scan flags any site with a problem; per-device rows still carry the detail
// of which device is the trouble.
//
// Exported so FleetDashboardPage can sort cards by severity using the same
// hierarchy the cards use to color themselves.
export const STATE_RANK: Record<DeviceState, number> = {
  solar_low_vin: 5,
  gray: 4,
  red: 3,
  yellow: 2,
  pending: 1,
  green: 0,
};

export function pickWorstState(
  devices: FleetDevice[],
  nowMs: number,
  alertIds: Set<number>,
): DeviceState | null {
  if (devices.length === 0) return null;
  let worst: DeviceState = 'green';
  for (const d of devices) {
    const s = pickDeviceState(d, nowMs, alertIds.has(d.id));
    if (STATE_RANK[s] > STATE_RANK[worst]) worst = s;
  }
  return worst;
}

// Single fleet-wide solar alert hook. Called once at the page level over the
// full device list so the hysteresis state is shared across every LocationCard
// (each card receives the resulting alertIds via prop). Returns the set of
// device.id values currently in the alert state. Hysteresis: enter at vin <
// 12.0 V, leave at vin > 12.1 V; a telemetry gap (vin_volts == null)
// preserves the prior state so a brief reporting hiccup doesn't flicker the
// alert off-on.
export function useSolarAlerts(devices: FleetDevice[]): Set<number> {
  const [alertIds, setAlertIds] = React.useState<Set<number>>(() => new Set());
  React.useEffect(() => {
    setAlertIds(prev => {
      const next = new Set(prev);
      const presentIds = new Set(devices.map(d => d.id));
      // Drop alert state for devices that left the list (assignment changed,
      // device deleted, etc.) — otherwise the set grows unbounded.
      prev.forEach(id => {
        if (!presentIds.has(id)) next.delete(id);
      });
      devices.forEach(d => {
        if (d.power_configuration !== 'Solar') {
          next.delete(d.id);
          return;
        }
        const v = d.vin_volts;
        if (v == null) return; // telemetry gap: preserve prior state
        const was = next.has(d.id);
        if (!was && v < SOLAR_BLINK_TRIGGER_V) next.add(d.id);
        else if (was && v > SOLAR_BLINK_CLEAR_V) next.delete(d.id);
      });
      // Identity-equal short-circuit: avoid forcing a re-render when the set
      // is unchanged. React's setState bails on === references but a freshly
      // constructed Set is never reference-equal, so check membership.
      if (next.size === prev.size && Array.from(next).every(id => prev.has(id))) {
        return prev;
      }
      return next;
    });
  }, [devices]);
  return alertIds;
}

interface DeviceRowProps {
  device: FleetDevice;
  events: FleetEventEntry[];
  nowMs: number;
  isSolarAlert: boolean;
  onOpenComments: (deviceId: number) => void;
  /** Extra top margin in pixels — used to align subsequent device rows to
      the single-card stride so a 2-device location's heartbeats land at the
      same y-positions as two stacked single-device cards. */
  extraTopMargin?: number;
}

const DeviceRow: React.FC<DeviceRowProps> = ({ device, events, nowMs, isSolarAlert, onOpenComments, extraTopMargin = 0 }) => {
  const state = pickDeviceState(device, nowMs, isSolarAlert);
  const stateTint = STATE_TINT[state];

  const label = device.label || device.coreid;

  const totals = React.useMemo(() => computeEventTotals(events, nowMs, 60, 24), [events, nowMs]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        bgcolor: stateTint,
        borderRadius: 0.5,
        px: 0.5,
        py: 0.25,
        mt: `${extraTopMargin}px`,
        animation: isSolarAlert && FLASH_ENABLED ? `${solarFlash} 2.8s step-end infinite` : 'none',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minHeight: 18 }}>
        <MuiLink
          component={RouterLink}
          to={`/devices/${device.id}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: device.label ? undefined : 'monospace',
            fontSize: 11,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'text.primary',
          }}
          title={device.label ? `${device.label} (${device.coreid})` : device.coreid}
        >
          {label}
        </MuiLink>
        {isSolarAlert && device.vin_volts != null && (
          <Typography
            component="span"
            sx={{
              color: STATE_COLOR.red,
              fontWeight: 700,
              fontSize: 11,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {device.vin_volts.toFixed(1)}v
          </Typography>
        )}
        <Tooltip title={device.comments_count === 0 ? 'No comments — click to add' : `${device.comments_count} comment${device.comments_count === 1 ? '' : 's'}`}>
          <IconButton
            size="small"
            onClick={() => onOpenComments(device.id)}
            aria-label={`Comments for ${device.coreid}`}
            sx={{ p: 0.25 }}
          >
            <Badge
              badgeContent={device.comments_count}
              color="primary"
              overlap="circular"
              max={99}
              sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14 } }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />
            </Badge>
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Charts row: heartbeat + volume on the left, vertical metric stack on
          the right. Right column carries ↑arrivals · total · ↓departures so
          they live alongside the bars instead of overlapping them. Tight gap
          (4 px) keeps the charts visually close to their associated numbers. */}
      <Stack direction="row" spacing="4px" alignItems="stretch">
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <HeartbeatSparkline
            publishes={device.publishes_24h}
            nowMs={nowMs}
            bucketSizeMinutes={60}
            bucketCount={24}
            bucketWidth={8}
            gap={1}
            height={HEARTBEAT_HEIGHT_PX}
          />
          <Box sx={{ height: `${CHART_SPACER_PX}px` }} />
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.2)',
              borderRadius: 0.25,
              height: EVENT_CHART_BOX_HEIGHT_PX,
              overflow: 'hidden',
            }}
          >
            <EventVolumeChart
              events={events}
              nowMs={nowMs}
              bucketSizeMinutes={60}
              bucketCount={24}
              bucketWidth={8}
              gap={1}
              height={HEARTBEAT_HEIGHT_PX}
            />
          </Box>
        </Box>

        <Stack
          direction="column"
          alignItems="flex-end"
          justifyContent="space-between"
          sx={{ width: 24, flexShrink: 0 }}
        >
          {/* Empty spacer item — adds a 4th flex child so space-between
              distributes the captions across 3 gaps (each ~7 px) instead of 2
              (each ~10.5 px), tightening ↑/↓ proportionally and dropping the
              beacon total off the top edge. */}
          <Box />
          <Tooltip title="Total beacons heard in the most recent closed bucket">
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                fontSize: 10,
                lineHeight: '12px',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {device.total_beacons}
            </Typography>
          </Tooltip>
          <Tooltip title={`${totals.arrivals} arrivals over the last 24 h`}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                fontSize: 10,
                lineHeight: '12px',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              ↑{totals.arrivals}
            </Typography>
          </Tooltip>
          <Tooltip title={`${totals.departures} departures over the last 24 h`}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                fontSize: 10,
                lineHeight: '12px',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              ↓{totals.departures}
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

interface LocationCardProps {
  locationName: string;
  devices: FleetDevice[];
  events: FleetEventEntry[];
  nowMs: number;
  /** Fleet-wide solar-alert IDs from the page-level useSolarAlerts hook. */
  alertIds: Set<number>;
  onOpenComments: (deviceId: number) => void;
}

// Span quantized to single-card units. Each device beyond the first needs
// its own unit because DEVICE_ROW_STRIDE_MARGIN consumes a full single-card
// stride per device:
//   1 device  → 8 rows  (1 unit)
//   2 devices → 16 rows (2 units)
//   N devices → N × 8 rows
function spanForDeviceCount(n: number): number {
  return Math.max(1, n) * ROWS_PER_UNIT;
}

export const LocationCard: React.FC<LocationCardProps> = ({
  locationName,
  devices,
  events,
  nowMs,
  alertIds,
  onOpenComments,
}) => {
  const span = spanForDeviceCount(devices.length);
  const worstState = pickWorstState(devices, nowMs, alertIds);
  const cardFlashing = worstState === 'solar_low_vin';

  return (
    <Card
      variant="outlined"
      sx={{
        gridRow: `span ${span}`,
        width: 272,
        p: 0.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        bgcolor: worstState ? STATE_TINT[worstState] : 'background.paper',
        borderColor: worstState ? STATE_COLOR[worstState] : 'rgba(0, 0, 0, 0.4)',
        borderWidth: 2,
        animation: cardFlashing && FLASH_ENABLED ? `${solarFlash} 2.8s step-end infinite` : 'none',
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        spacing={0.75}
        sx={{ minWidth: 0, px: 0.25 }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            fontSize: 12,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={locationName}
        >
          {locationName}
        </Typography>
        {devices.length > 1 && (
          // Match subtitle2's lineHeight so the count caption's line box fits
          // inside the title's. Without this, MUI's default caption lineHeight
          // (1.66) makes the title row 16.6 px tall in multi-device cards
          // versus 14.4 px in single-device cards — a 2 px constant offset
          // that shifts every device row down relative to single cards.
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, lineHeight: 1.2 }}>
            {devices.length}
          </Typography>
        )}
      </Stack>

      {devices.map((device, idx) => (
        <DeviceRow
          key={device.id}
          device={device}
          events={events}
          nowMs={nowMs}
          isSolarAlert={alertIds.has(device.id)}
          onOpenComments={onOpenComments}
          // Subsequent device rows get extra top margin so each row's heartbeat
          // sits at the same y-position it would occupy if its device were in
          // its own single-device card stacked below the previous one. Aligns
          // the multi-device card's internal layout with the surrounding grid
          // for the 2-device case; tighter device counts overflow gracefully.
          extraTopMargin={idx > 0 ? DEVICE_ROW_STRIDE_MARGIN : 0}
        />
      ))}
    </Card>
  );
};
